// src/PasswordManagement.js

import React, { useMemo, useState, useCallback, useEffect } from 'react';

// Hooks
import usePasswordFetch from './features/passwords/hooks/usePasswordFetch';
import usePasswordDerived from './features/passwords/hooks/usePasswordDerived';
import usePasswordEditing from './features/passwords/hooks/usePasswordEditing';
import usePasswordBulk from './features/passwords/hooks/usePasswordBulk';
import useRealtimeUpdates from './features/passwords/hooks/useRealtimeUpdates';
import { useDebouncedCallback } from './hooks/useDebounce';

// Components
import {
  BulkForm,
  Table,
  SearchBar,
  Toast,
} from './features/passwords/components';

// Utils
import { paginate } from './features/passwords/utils/pagination';

// API
import api, { fetchUniqueSites } from './api';
import { usePortActivationStatus, PortActivationBadge } from './shared/portActivation';

function PasswordManagement() {
  // ----------------------------------------------------------------
  // Fetch (this page is the main passwords page → not advice)
  // ----------------------------------------------------------------
  const { passwords, ports, usersMap, loading, error, refetch } = usePasswordFetch({
    advice: false,
  });

  // ----------------------------------------------------------------
  // Local UI state
  // ----------------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(null); // 'subscribed' | 'onboarded' | null
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [expandedUserCode, setExpandedUserCode] = useState(null);
  const [notesOpenMap, setNotesOpenMap] = useState({});

  // Port activation status (hardware-first, polled + websocket)
  const { portStatus, refresh: refreshPortActivation } = usePortActivationStatus({
    scope: 'staff',
  });
  const [portCountdowns, setPortCountdowns] = useState({});
  const [sites, setSites] = useState([]);
  
  // Missing passwords comparison state
  const [missingPasswords, setMissingPasswords] = useState([]);
  const [checkingMissing, setCheckingMissing] = useState(false);
  const [showMissingModal, setShowMissingModal] = useState(false);

  // ----------------------------------------------------------------
  // Derived data (filter → group → sort → paginate)
  // ----------------------------------------------------------------
  const {
    userCodeMainEntries,
    sortedUserCodeMainEntries,
  } = usePasswordDerived({
    passwords,
    users: usersMap,
    ports,
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
  });

  const paginatedEntries = useMemo(
    () => paginate(sortedUserCodeMainEntries, currentPage, itemsPerPage),
    [sortedUserCodeMainEntries, currentPage, itemsPerPage]
  );

  // Auto-expand if exactly one user appears
  React.useEffect(() => {
    if (userCodeMainEntries.length === 1) {
      setExpandedUserCode(userCodeMainEntries[0].userCode);
    }
  }, [userCodeMainEntries]);

  // Fetch sites on mount
  React.useEffect(() => {
    fetchUniqueSites()
      .then(setSites)
      .catch(() => setSites([]));
  }, []);

  // ----------------------------------------------------------------
  // Editing logic (child rows + main row) with multilogin enabled
  // ----------------------------------------------------------------
  const {
    // feedback
    error: editError,
    toastMessage,
    showToast,

    // child editing
    editingId,
    editDraft,
    changeEditField,
    startEditingRow,
    cancelEditingRow,
    saveEditingRow,
    saveAndApplyAll,

    // address editor
    showAddressEditor,
    setShowAddressEditor,
    addressParts,
    setAddressParts,
    parseAddressIntoParts,
    handleAddressSave,

    // child create/delete
    addingNewRecord,
    setAddingNewRecord,
    newRecordUserCode,
    setNewRecordUserCode,
    newRecordDraft,
    setNewRecordDraft,
    createNewRecord,
    deleteRecord,

    // main row editing
    editingMainRow,
    setEditingMainRow,
    mainRowEditDraft,
    setMainRowEditDraft,
    startEditingMainRow,
    saveMainRow,

    // notes
    saveNotes,
  } = usePasswordEditing({
    resourcePrefix: '/passwords',
    refresh: refetch,
    withMultilogin: true,
  });

  // ----------------------------------------------------------------
  // Bulk form logic
  // ----------------------------------------------------------------
  const {
    showNewPasswordForm,
    setShowNewPasswordForm,
    error: bulkError,
    toastMessage: bulkToast,
    newPasswordEntries,
    handleNewPasswordFieldChange,
    addNewPasswordEntry,
    removePasswordEntry,
    resetNewPasswordForm,
    invalidRows,
    hasValidationIssues,
    handleCreateNewPasswords,
  } = usePasswordBulk({
    resourcePrefix: '/passwords',
    refresh: refetch,
  });

  // ----------------------------------------------------------------
  // Realtime updates (debounced for relevant events only)
  // ----------------------------------------------------------------
  // Debounced refetch - max once per 3 seconds
  const debouncedRefetch = useDebouncedCallback(() => {
    console.log('Debounced refetch triggered');
    refetch();
  }, 3000);

  useRealtimeUpdates(
    {
      onPassword: () => {
        console.log('Password update received, debouncing refetch...');
        debouncedRefetch();
      },
      onUser: () => {
        console.log('User update received, debouncing refetch...');
        debouncedRefetch();
      },
      // Don't refetch on port updates - not relevant to password management
    },
    [debouncedRefetch]
  );

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------
  const toggleNotes = useCallback((userCode) => {
    if (expandedUserCode !== userCode) {
      setExpandedUserCode(userCode);
    }
    setNotesOpenMap((prev) => ({ ...prev, [userCode]: !prev[userCode] }));
  }, [expandedUserCode]);

  const handleSortBySubscribed = useCallback((e) => {
    e?.stopPropagation?.();
    setSortDirection((prev) => {
      if (sortField === 'subscribed') {
        return prev === 'asc' ? 'desc' : 'asc';
      }
      return 'desc'; // default to putting 'true' on top
    });
    setSortField('subscribed');
    setCurrentPage(1);
  }, [sortField]);

  const handleSortByOnboarded = useCallback((e) => {
    e?.stopPropagation?.();
    setSortDirection((prev) => {
      if (sortField === 'onboarded') {
        return prev === 'asc' ? 'desc' : 'asc';
      }
      return 'desc';
    });
    setSortField('onboarded');
    setCurrentPage(1);
  }, [sortField]);

  // --- helper to normalise phone numbers to digits ---
  const digits = (s) => String(s || '').replace(/\D/g, '');

  // UPDATED: wire up "Activate" button to enqueue activation with queue semantics
  const handleActivatePort = useCallback(async (phoneNumber) => {
    if (!phoneNumber) return;

    const match = ports.find(
      (p) => digits(p.assigned_number) === digits(phoneNumber)
    );

    if (!match || !match.port_number || !match.id) {
      showToast(`No port mapped to ${phoneNumber}`);
      return;
    }

    // Check current queue status before activating
    try {
      const queueCheck = await api.get(`/api/ports/${match.id}/activation-queue`);
      const existingQueue = queueCheck?.data?.items || [];
      const alreadyQueued = existingQueue.find(
        item => item.target_port_id === match.id && item.status === 'queued'
      );
      
      if (alreadyQueued) {
        showToast(`⚠ Already in queue for ${phoneNumber}`);
        return;
      }
    } catch (err) {
      // Queue check failed, continue anyway
    }

    try {
      const res = await api.post(`/api/ports/${match.id}/activate`, { number: phoneNumber });
      const info = res?.data || {};
      if (info.status === 'active') {
        showToast(`✓ Activated now: ${phoneNumber} on ${match.port_number}`);
      } else if (info.status === 'already_queued') {
        showToast(`⚠ Already queued: ${phoneNumber}`);
      } else if (info.status === 'queued') {
        const pos = info.position ?? 1;
        const eta = info.eta_seconds ?? 60;
        showToast(`⏳ Queued (position ${pos}) — ETA ~${Math.ceil(eta)}s`);
      }
      // refresh status view soon after
      refreshPortActivation();
    } catch (err) {
      console.error('Port activation failed:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Failed to enqueue activation';
      showToast(`✗ ${msg}`);
    }
  }, [ports, showToast, refreshPortActivation]);

  const getStatusBadge = useCallback(
    (port) => (
      <PortActivationBadge
        statusEntry={portStatus[port?.id]}
        countdown={portCountdowns[port?.id]}
        compact
        showTrafficLight={false}
      />
    ),
    [portStatus, portCountdowns]
  );

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading) return <div>Loading...</div>;
  const displayError = editError || bulkError || error;
  if (displayError) {
    return <div className="alert alert-danger py-2">{String(displayError)}</div>;
  }

  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      <h4 className="mb-3" style={{ fontSize: '18px' }}>
        Password Management
      </h4>

      {/* Global toast messages */}
      {(toastMessage || bulkToast) && (
        <Toast message={toastMessage || bulkToast} />
      )}

      {/* Search + “Add New Entries” */}
      <div className="row g-2 mb-2">
        <div className="col-md-4">
          <SearchBar
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v);
              setCurrentPage(1);
            }}
            placeholder="Search user code, full name, email, phone, or Slack ID..."
          />
        </div>
        <div className="col-md-8 d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowNewPasswordForm(true)}
            disabled={showNewPasswordForm}
          >
            Add New Password Entries
          </button>
          <button
            className="btn btn-info btn-sm"
            onClick={async () => {
              setCheckingMissing(true);
              try {
                const response = await api.get('/passwords/missing-in-advice');
                setMissingPasswords(response.data || []);
                setShowMissingModal(true);
              } catch (err) {
                alert(`Error checking missing passwords: ${err.response?.data?.detail || err.message}`);
              } finally {
                setCheckingMissing(false);
              }
            }}
            disabled={checkingMissing}
          >
            {checkingMissing ? 'Checking...' : 'Find Missing in Advice Passwords'}
          </button>
        </div>
      </div>

      {/* Bulk form (visible when showNewPasswordForm is true) */}
      <BulkForm
        show={showNewPasswordForm}
        onClose={() => {
          setShowNewPasswordForm(false);
          resetNewPasswordForm();
        }}
        entries={newPasswordEntries}
        onFieldChange={handleNewPasswordFieldChange}
        onAddRow={addNewPasswordEntry}
        onRemoveRow={removePasswordEntry}
        onSubmit={handleCreateNewPasswords}
        invalidRows={invalidRows}
        hasValidationIssues={hasValidationIssues}
        sites={sites}
        isStaff={true}
      />

      {/* Modal for missing passwords */}
      {showMissingModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Passwords Missing in Advice Passwords ({missingPasswords.length} found)
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMissingModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {missingPasswords.length === 0 ? (
                  <p className="text-success">All passwords are present in advice passwords!</p>
                ) : (
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>User Code</th>
                        <th>Email</th>
                        <th>Site</th>
                        <th>Password</th>
                        <th>Main</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingPasswords.map((pwd) => (
                        <tr key={pwd.id}>
                          <td>{pwd.id}</td>
                          <td>{pwd.user_code}</td>
                          <td>{pwd.email || '(empty)'}</td>
                          <td>{pwd.site || '(empty)'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                            {pwd.password || '(empty)'}
                          </td>
                          <td>{pwd.main ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMissingModal(false)}
                >
                  Close
                </button>
                {missingPasswords.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const csv = [
                        ['ID', 'User Code', 'Email', 'Site', 'Password', 'Main'],
                        ...missingPasswords.map(p => [
                          p.id,
                          p.user_code || '',
                          p.email || '',
                          p.site || '',
                          p.password || '',
                          p.main ? 'Yes' : 'No'
                        ])
                      ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `missing-passwords-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Export to CSV
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main table with expandable child rows */}
      <Table
        entries={paginatedEntries}
        expandedUserCode={expandedUserCode}
        setExpandedUserCode={setExpandedUserCode}
        notesOpenMap={notesOpenMap}
        toggleNotes={toggleNotes}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortSubscribed={handleSortBySubscribed}
        onSortOnboarded={handleSortByOnboarded}
        editingMainRow={editingMainRow}
        mainRowEditDraft={mainRowEditDraft}
        setMainRowEditDraft={setMainRowEditDraft}
        onStartEditingMainRow={startEditingMainRow}
        onSaveMainRow={saveMainRow}
        onCancelMainRow={() => setEditingMainRow(null)}
        withMultilogin={true}
        getStatusBadge={getStatusBadge}
        childProps={{
          // ✅ Pass ports down for ChildRow status matching
          ports: ports,
          // child row props forwarded to ExpandedRows
          users: usersMap,
          onStartEditingRow: (entry) => startEditingRow(entry, usersMap),
          editingId,
          editDraft,
          onChangeEditField: changeEditField,
          onCancelEditingRow: cancelEditingRow,
          onSaveEditingRow: saveEditingRow,
          onSaveAndApplyAll: (entry) =>
            saveAndApplyAll(entry, { multilogin: mainRowEditDraft.multilogin }),
          showAddressEditor,
          setShowAddressEditor,
          addressParts,
          setAddressParts,
          parseAddressIntoParts,
          handleAddressSave,
          addingNewRecord,
          setAddingNewRecord,
          newRecordUserCode,
          setNewRecordUserCode,
          newRecordDraft,
          setNewRecordDraft,
          onCreateNewRecord: createNewRecord,
          sites: sites,
          isStaff: true,
          onDeleteRecord: deleteRecord,
          onSaveNotes: saveNotes,
          // Pass the activator down so the "Activate" buttons work with the queue
          onActivatePort: handleActivatePort,
        }}
      />

      {/* Pagination footer */}
      {sortedUserCodeMainEntries.length > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, sortedUserCodeMainEntries.length)} of{' '}
            {sortedUserCodeMainEntries.length} entries
          </div>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                  First
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((c) => Math.max(c - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {Array.from({ length: Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage) }).map((_, i) => {
                const pageNumber = i + 1;
                return (
                  <li
                    key={pageNumber}
                    className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                      {pageNumber}
                    </button>
                  </li>
                );
              })}
              <li
                className={`page-item ${
                  currentPage === Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage) ||
                  sortedUserCodeMainEntries.length === 0
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((c) =>
                      Math.min(c + 1, Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage))
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage) ||
                    sortedUserCodeMainEntries.length === 0
                  }
                >
                  Next
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage) ||
                  sortedUserCodeMainEntries.length === 0
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage))}
                  disabled={
                    currentPage === Math.ceil(sortedUserCodeMainEntries.length / itemsPerPage) ||
                    sortedUserCodeMainEntries.length === 0
                  }
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

export default PasswordManagement;
